/**
 * Done. CPD — Dashboard Completed Modules section (Stitch v2 / screen 7)
 *
 * Module library API is not yet shipped, so we surface the user's logged
 * activities as completed rows. Each row links to /export (certificate) and
 * /activities (reflections). Empty state when no activities exist.
 */

import { Link } from 'wouter';
import type { Activity } from '@/lib/types';
import { formatDate, formatHours } from '@/lib/utils';

interface CompletedModulesProps {
  activities: Activity[];
}

export function CompletedModules({ activities }: CompletedModulesProps): React.ReactElement {
  return (
    <section aria-label="Completed modules">
      <div className="flex items-center gap-3 mb-6">
        <span className="material-symbols-outlined text-[var(--stitch-surface-tint)]">check_circle</span>
        <h2 className="stitch-font-headline text-[24px] text-[var(--stitch-primary)] tracking-tight">
          Completed Modules
        </h2>
      </div>

      {activities.length === 0 ? (
        <div className="bg-[var(--stitch-surface-container-lowest)] border border-dashed border-[var(--stitch-surface-container-high)] p-10 rounded-xl flex flex-col items-center text-center">
          <span className="material-symbols-outlined text-[var(--stitch-outline)] text-4xl mb-3">checklist</span>
          <p className="stitch-font-body text-[var(--stitch-on-surface-variant)]">
            No completed activities yet
          </p>
          <Link
            href="/activities"
            className="mt-3 stitch-font-label text-[12px] text-[var(--stitch-surface-tint)] hover:text-[var(--stitch-primary)] uppercase tracking-wider transition-colors"
          >
            Log your first activity →
          </Link>
        </div>
      ) : (
        <div className="space-y-4">
          {activities.map(activity => (
            <div
              key={activity.id}
              className="bg-[var(--stitch-surface-container-low)]/60 border border-[var(--stitch-surface-container-high)] p-6 rounded-xl flex flex-col md:flex-row justify-between items-start md:items-center gap-4 hover:bg-[var(--stitch-surface-container-low)] transition-colors"
            >
              <div className="min-w-0">
                <h4 className="stitch-font-headline text-[18px] text-[var(--stitch-primary)] mb-1 truncate">
                  {activity.title}
                </h4>
                <p className="stitch-font-body text-sm text-[var(--stitch-on-surface-variant)] opacity-80">
                  Completed {formatDate(activity.activityDate)} • {formatHours(activity.hours)} Hours
                </p>
              </div>
              <div className="flex gap-6 shrink-0 items-center">
                {activity.sourceUrl && (
                  <a
                    href={activity.sourceUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    title={activity.sourceUrl}
                    className="text-[var(--stitch-on-surface-variant)] hover:text-[var(--stitch-primary)] transition-colors"
                    aria-label="Open source URL in a new tab"
                  >
                    <span className="material-symbols-outlined text-lg">open_in_new</span>
                  </a>
                )}
                {activity.certificateKey && (
                  <a
                    href={`/api/activities/${activity.id}/certificate`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[var(--stitch-primary)] stitch-font-label text-[12px] flex items-center gap-2 group uppercase tracking-wider"
                  >
                    <span className="material-symbols-outlined text-xl group-hover:translate-y-0.5 transition-transform">
                      download
                    </span>
                    Certificate
                  </a>
                )}
                {!activity.certificateKey && activity.linkedModuleId && (
                  <Link
                    href={`/cert/${activity.linkedModuleId}`}
                    className="text-[var(--stitch-primary)] stitch-font-label text-[12px] flex items-center gap-2 group uppercase tracking-wider"
                  >
                    <span className="material-symbols-outlined text-xl group-hover:translate-y-0.5 transition-transform">
                      download
                    </span>
                    Certificate
                  </Link>
                )}
                {activity.hasReflection ? (
                  <Link
                    href="/activities"
                    className="text-[var(--stitch-primary)] stitch-font-label text-[12px] flex items-center gap-2 group uppercase tracking-wider"
                    aria-label="View saved reflection"
                  >
                    <span className="material-symbols-outlined is-filled text-xl text-green-700">
                      task_alt
                    </span>
                    Reflection saved
                  </Link>
                ) : (
                  <Link
                    href="/activities"
                    className="text-[var(--stitch-on-surface-variant)] stitch-font-label text-[12px] flex items-center gap-2 group uppercase tracking-wider hover:text-[var(--stitch-primary)] transition-colors"
                    aria-label="Add a reflection"
                  >
                    <span className="material-symbols-outlined text-xl">edit_note</span>
                    Add reflection
                  </Link>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}
